/**
 * Route table.
 *
 * Every feature route is lazy, so each becomes its own chunk and the initial bundle
 * stays small. Every top-level branch has an errorElement so a crash in one feature
 * does not blank the shell.
 */

import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { AppShell } from '@/app/layouts/AppShell'
import { AuthLayout } from '@/app/layouts/AuthLayout'
import { RouteErrorBoundary } from './RouteError'
import { FullPageLoader, RedirectIfAuthenticated, RequireAuth, RequirePermission } from './guards'
import { PERMISSIONS, type Permission } from '@/lib/permissions'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const SelectTenantPage = lazy(() => import('@/features/auth/SelectTenantPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const InspectionsPage = lazy(() => import('@/features/inspections/InspectionsPage'))
const InspectionDetailPage = lazy(() => import('@/features/inspections/InspectionDetailPage'))
const IssuesPage = lazy(() => import('@/features/issues/IssuesPage'))
const ProjectsPage = lazy(() => import('@/features/properties/ProjectsPage'))
const ProjectDetailPage = lazy(() => import('@/features/properties/ProjectDetailPage'))
const UnitsPage = lazy(() => import('@/features/properties/UnitsPage'))
const UnitDetailPage = lazy(() => import('@/features/properties/UnitDetailPage'))
const TemplatesPage = lazy(() => import('@/features/templates/TemplatesPage'))
const TemplateBuilderPage = lazy(() => import('@/features/templates/TemplateBuilderPage'))
const MembersPage = lazy(() => import('@/features/people/MembersPage'))
const MemberDetailPage = lazy(() => import('@/features/people/MemberDetailPage'))
const InspectorsPage = lazy(() => import('@/features/people/InspectorsPage'))
const RolesPage = lazy(() => import('@/features/people/RolesPage'))
const OrganisationPage = lazy(() => import('@/features/settings/OrganisationPage'))
const BrandingPage = lazy(() => import('@/features/settings/BrandingPage'))
const TaxonomiesPage = lazy(() => import('@/features/settings/TaxonomiesPage'))
const AuditPage = lazy(() => import('@/features/audit/AuditPage'))
const AccessCodesPage = lazy(() => import('@/features/access-codes/AccessCodesPage'))
const MappingPage = lazy(() => import('@/features/mapping/MappingPage'))
const MeasurementScopesPage = lazy(() => import('@/features/measurements/MeasurementScopesPage'))
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'))
const NotFoundPage = lazy(() => import('@/features/shared/NotFoundPage'))

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullPageLoader />}>{children}</Suspense>
}

/** Wrap a lazy element with its permission requirement in one place. */
function guarded(element: ReactNode, any: Permission[]): ReactNode {
  return (
    <RequirePermission any={any}>
      <Lazy>{element}</Lazy>
    </RequirePermission>
  )
}

const routes: RouteObject[] = [
  {
    element: (
      <RedirectIfAuthenticated>
        <AuthLayout />
      </RedirectIfAuthenticated>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/login', element: <Lazy><LoginPage /></Lazy> },
      { path: '/select-tenant', element: <Lazy><SelectTenantPage /></Lazy> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <Lazy><DashboardPage /></Lazy> },

      {
        path: '/inspections',
        element: guarded(<InspectionsPage />, [PERMISSIONS.INSPECTION_VIEW]),
      },
      {
        path: '/inspections/:inspectionId',
        element: guarded(<InspectionDetailPage />, [PERMISSIONS.INSPECTION_VIEW]),
      },
      { path: '/issues', element: guarded(<IssuesPage />, [PERMISSIONS.ISSUE_VIEW]) },
      {
        path: '/access-codes',
        element: guarded(<AccessCodesPage />, [PERMISSIONS.ACCESS_CODE_VIEW]),
      },

      {
        path: '/properties/projects',
        element: guarded(<ProjectsPage />, [PERMISSIONS.PROPERTY_VIEW]),
      },
      {
        path: '/properties/projects/:projectId',
        element: guarded(<ProjectDetailPage />, [PERMISSIONS.PROPERTY_VIEW]),
      },
      { path: '/properties/units', element: guarded(<UnitsPage />, [PERMISSIONS.PROPERTY_VIEW]) },
      {
        path: '/properties/units/:unitId',
        element: guarded(<UnitDetailPage />, [PERMISSIONS.PROPERTY_VIEW]),
      },

      { path: '/templates', element: guarded(<TemplatesPage />, [PERMISSIONS.TEMPLATE_VIEW]) },
      // Declared BEFORE /templates/:templateId so "mapping" is not read as an id.
      {
        path: '/templates/mapping',
        element: guarded(<MappingPage />, [PERMISSIONS.TEMPLATE_VIEW]),
      },
      {
        path: '/measurements',
        element: guarded(<MeasurementScopesPage />, [PERMISSIONS.MEASUREMENT_SCOPE_VIEW]),
      },
      { path: '/reports', element: guarded(<ReportsPage />, [PERMISSIONS.REPORT_VIEW]) },
      {
        path: '/templates/:templateId',
        element: guarded(<TemplateBuilderPage />, [PERMISSIONS.TEMPLATE_VIEW]),
      },

      { path: '/people/members', element: guarded(<MembersPage />, [PERMISSIONS.MEMBER_VIEW]) },
      {
        path: '/people/members/:membershipId',
        element: guarded(<MemberDetailPage />, [PERMISSIONS.MEMBER_VIEW]),
      },
      { path: '/people/inspectors', element: guarded(<InspectorsPage />, [PERMISSIONS.MEMBER_VIEW]) },
      { path: '/settings/roles', element: guarded(<RolesPage />, [PERMISSIONS.MEMBER_VIEW]) },

      {
        path: '/settings/organisation',
        element: guarded(<OrganisationPage />, [PERMISSIONS.SETTINGS_MANAGE]),
      },
      {
        path: '/settings/branding',
        element: guarded(<BrandingPage />, [PERMISSIONS.SETTINGS_MANAGE]),
      },
      {
        path: '/settings/taxonomies',
        element: guarded(<TaxonomiesPage />, [PERMISSIONS.SETTINGS_MANAGE]),
      },

      { path: '/audit', element: guarded(<AuditPage />, [PERMISSIONS.AUDIT_VIEW]) },

      { path: '*', element: <Lazy><NotFoundPage /></Lazy> },
    ],
  },
]

export const router = createBrowserRouter(routes)
